import { Card as CardType, ListStyle } from '@yleisradio/areena-types';
import { Link } from 'components/Link';
import React from 'react';
import { filterLabelsByType, findLabel } from 'utils/card';
import { sanitizeHtmlId } from 'utils/ui-helpers';
import styles from './SearchHorizontalCard.module.scss';
import { CardImage } from './CardImage';
import { CardLabels } from './CardLabels';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

type Props = {
  card: CardType;
  listStyle?: ListStyle | undefined;
  cardKey: string;
  sizes: string;
  onClick(): void;
};

const getAspectRatioClassName = (listStyle?: ListStyle | undefined) => {
  switch (listStyle?.image) {
    case '1:1':
      return styles.withAspectRatio1_1;
    case '16:9':
    default:
      return styles.withAspectRatio16_9;
  }
};

export const SearchHorizontalCard: React.FunctionComponent<Props> = ({
  card,
  listStyle,
  cardKey,
  sizes,
  onClick,
}) => {
  const { pointer } = card;

  const accessibilityDescription = findLabel(
    card.labels,
    'accessibilityDescription'
  )?.formatted;
  const genericLabels = filterLabelsByType('generic', card.labels);

  const accessibilityDescriptionId =
    accessibilityDescription &&
    sanitizeHtmlId(cardKey + 'accessibilityDescription');
  const titleId = sanitizeHtmlId(cardKey + 'title');

  return (
    <div
      aria-labelledby={titleId}
      aria-describedby={accessibilityDescriptionId}
      className={styles.searchCardHorizontal}
      role="group"
    >
      {listStyle?.image !== 'none' ? (
        <div className={getAspectRatioClassName(listStyle)}>
          <CardImage
            image={card.image}
            imageStyle={listStyle?.image}
            sizes={sizes}
          />
        </div>
      ) : null}

      <div className={styles.textContainer}>
        <div className={styles.title} id={titleId}>
          {pointer ? (
            <Link
              className={styles.searchCardLink}
              pointer={pointer}
              onClick={onClick}
            >
              {card.title}
            </Link>
          ) : (
            card.title
          )}
        </div>

        {card.description && (
          <div className={styles.description}>{card.description}</div>
        )}

        {genericLabels.length > 0 && (
          <div className={styles.labels}>
            <CardLabels labels={genericLabels} />
          </div>
        )}
      </div>

      {accessibilityDescription ? (
        <VisuallyHidden id={accessibilityDescriptionId}>
          {accessibilityDescription}
        </VisuallyHidden>
      ) : null}
    </div>
  );
};
